import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getCapacityPlanning, getCapacityHeatmap, getTimeAnalytics } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Users,
  ChartLine,
  Clock,
  CalendarBlank,
  TrendUp,
  TrendDown,
  Warning,
  CheckCircle,
  User,
  Folder,
  Lightning,
  ChartBar,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Status Badge Component
function CapacityBadge({ status }) {
  const { isSpanish } = useLanguage();
  
  const config = {
    available: { color: 'bg-emerald-500/20 text-emerald-400', label: isSpanish ? 'Disponible' : 'Available' },
    optimal: { color: 'bg-blue-500/20 text-blue-400', label: isSpanish ? 'Óptimo' : 'Optimal' },
    overbooked: { color: 'bg-red-500/20 text-red-400', label: isSpanish ? 'Sobrecargado' : 'Overbooked' },
    underutilized: { color: 'bg-amber-500/20 text-amber-400', label: isSpanish ? 'Subutilizado' : 'Underutilized' },
  };
  
  const c = config[status] || config.available;
  return <Badge className={c.color}>{c.label}</Badge>;
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, color = 'cyan' }) {
  const colorClasses = {
    cyan: 'from-[#3AB8D6]/20 to-[#3AB8D6]/5 border-[#3AB8D6]/30',
    gold: 'from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30',
    green: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30',
    purple: 'from-purple-500/20 to-purple-500/5 border-purple-500/30',
  };
  
  return (
    <div className={`valhalla-card p-5 bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-[#9CA3AF]" />
      </div>
      <p className="text-2xl font-bold text-[#F3F4F6]">{value}</p>
      <p className="text-xs text-[#9CA3AF] mt-1">{label}</p>
      {subValue && <p className="text-xs text-[#6B7280] mt-0.5">{subValue}</p>}
    </div>
  );
}

// Capacity Overview Tab
function CapacityOverview({ data, loading }) {
  const { t, isSpanish } = useLanguage();
  
  if (loading || !data) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label={isSpanish ? 'Total Empleados' : 'Total Employees'} value={data.summary.total_employees} color="cyan" />
        <StatCard icon={CheckCircle} label={isSpanish ? 'Capacidad Óptima' : 'Optimal'} value={data.summary.optimal} color="green" />
        <StatCard icon={Warning} label={isSpanish ? 'Sobrecargados' : 'Overbooked'} value={data.summary.overbooked} color="red" />
        <StatCard icon={TrendDown} label={isSpanish ? 'Subutilizados' : 'Underutilized'} value={data.summary.underutilized} color="gold" />
      </div>

      {/* Employees Table */}
      <div className="valhalla-card overflow-hidden">
        <div className="bg-[#0B0E14] px-6 py-4 border-b border-[#242E42]">
          <h3 className="text-lg font-semibold text-[#F3F4F6]">{isSpanish ? 'Planificación de Recursos' : 'Resource Planning'}</h3>
        </div>
        <table className="w-full">
          <thead className="bg-[#0B0E14]/50">
            <tr>
              <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Empleado' : 'Employee'}</th>
              <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Proyectos' : 'Projects'}</th>
              <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Capacidad Sem.' : 'Weekly Cap.'}</th>
              {data.employees[0]?.weekly_data?.map((w, idx) => (
                <th key={idx} className="text-center py-4 px-3 text-[#9CA3AF] font-medium text-xs">
                  {isSpanish ? `Sem ${w.week}` : `Wk ${w.week}`}
                </th>
              ))}
              <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Prom.' : 'Avg.'}</th>
              <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('status')}</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map((emp) => (
              <tr key={emp.employee_id} className="border-t border-[#242E42] hover:bg-[#0B0E14]/50">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-semibold text-sm">
                      {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="text-[#F3F4F6] font-medium">{emp.name}</p>
                      <p className="text-xs text-[#9CA3AF]">{emp.position}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6 text-center text-[#9CA3AF]">{emp.assigned_projects.length}</td>
                <td className="py-4 px-6 text-center text-[#F3F4F6]">{emp.weekly_capacity}h</td>
                {emp.weekly_data.map((w, idx) => {
                  const bgColor = w.allocation_pct >= 90 ? 'bg-red-500' : w.allocation_pct >= 60 ? 'bg-blue-500' : 'bg-emerald-500';
                  const opacity = Math.min(w.allocation_pct / 100, 1);
                  return (
                    <td key={idx} className="py-4 px-3 text-center">
                      <div className="relative w-12 h-8 mx-auto rounded flex items-center justify-center" style={{ backgroundColor: `rgba(${w.allocation_pct >= 90 ? '239, 68, 68' : w.allocation_pct >= 60 ? '59, 130, 246' : '16, 185, 129'}, ${opacity * 0.3})` }}>
                        <span className="text-xs font-medium text-[#F3F4F6]">{w.allocation_pct}%</span>
                      </div>
                    </td>
                  );
                })}
                <td className="py-4 px-6 text-center">
                  <span className={`font-semibold ${emp.avg_allocation >= 85 ? 'text-red-400' : emp.avg_allocation >= 50 ? 'text-blue-400' : 'text-amber-400'}`}>
                    {emp.avg_allocation}%
                  </span>
                </td>
                <td className="py-4 px-6 text-center">
                  <CapacityBadge status={emp.overall_status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-500/30"></div>
          <span className="text-[#9CA3AF]">&lt;60% - {isSpanish ? 'Disponible' : 'Available'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500/30"></div>
          <span className="text-[#9CA3AF]">60-90% - {isSpanish ? 'Óptimo' : 'Optimal'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500/30"></div>
          <span className="text-[#9CA3AF]">&gt;90% - {isSpanish ? 'Sobrecargado' : 'Overbooked'}</span>
        </div>
      </div>
    </div>
  );
}

// Heatmap Tab
function CapacityHeatmapTab({ heatmapData, loading }) {
  const { t, isSpanish } = useLanguage();
  
  if (loading || !heatmapData) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }
  
  const getColor = (value) => {
    if (value >= 90) return 'bg-red-500';
    if (value >= 75) return 'bg-orange-500';
    if (value >= 60) return 'bg-blue-500';
    if (value >= 40) return 'bg-cyan-500';
    if (value >= 20) return 'bg-emerald-500';
    return 'bg-gray-600';
  };
  
  return (
    <div className="space-y-6">
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-6 flex items-center gap-2">
          <ChartBar className="w-5 h-5 text-[#E2B05E]" />
          {isSpanish ? 'Mapa de Calor de Capacidad' : 'Capacity Heatmap'}
        </h3>
        
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Header - Weeks */}
            <div className="flex mb-2">
              <div className="w-48 flex-shrink-0"></div>
              {heatmapData.employees[0]?.weeks.map((w, idx) => (
                <div key={idx} className="w-16 text-center text-xs text-[#9CA3AF]">
                  {w.week_label}
                </div>
              ))}
            </div>
            
            {/* Rows - Employees */}
            {heatmapData.employees.map((emp) => (
              <div key={emp.employee_id} className="flex items-center mb-1">
                <div className="w-48 flex-shrink-0 pr-4">
                  <p className="text-sm text-[#F3F4F6] truncate">{emp.name}</p>
                  <p className="text-xs text-[#6B7280]">{emp.position}</p>
                </div>
                {emp.weeks.map((w, idx) => (
                  <div key={idx} className="w-16 px-0.5">
                    <div
                      className={`h-8 rounded ${getColor(w.value)} flex items-center justify-center transition-all hover:scale-105 cursor-default`}
                      title={`${emp.name}: ${w.value}% - ${w.week_label}`}
                      style={{ opacity: Math.max(0.3, w.value / 100) }}
                    >
                      <span className="text-xs font-medium text-white">{w.value > 0 ? w.value : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-6 text-sm">
          <span className="text-[#9CA3AF]">{isSpanish ? 'Utilización:' : 'Utilization:'}</span>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-gray-600"></div><span className="text-[#9CA3AF]">0-20%</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-500"></div><span className="text-[#9CA3AF]">20-40%</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-cyan-500"></div><span className="text-[#9CA3AF]">40-60%</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-500"></div><span className="text-[#9CA3AF]">60-75%</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-orange-500"></div><span className="text-[#9CA3AF]">75-90%</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500"></div><span className="text-[#9CA3AF]">&gt;90%</span></div>
        </div>
      </div>
    </div>
  );
}

// Time Analytics Tab
function TimeAnalyticsTab({ analyticsData, loading, period, onPeriodChange }) {
  const { t, isSpanish } = useLanguage();
  
  if (loading || !analyticsData) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }
  
  const billableCategories = analyticsData.by_category.filter(c => c.billable);
  const nonBillableCategories = analyticsData.by_category.filter(c => !c.billable);
  
  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-end">
        <Select value={period} onValueChange={onPeriodChange}>
          <SelectTrigger className="w-40 valhalla-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            <SelectItem value="week">{isSpanish ? 'Esta Semana' : 'This Week'}</SelectItem>
            <SelectItem value="month">{isSpanish ? 'Este Mes' : 'This Month'}</SelectItem>
            <SelectItem value="quarter">{isSpanish ? 'Este Trimestre' : 'This Quarter'}</SelectItem>
            <SelectItem value="year">{isSpanish ? 'Este Año' : 'This Year'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Clock} label={isSpanish ? 'Horas Totales' : 'Total Hours'} value={analyticsData.total_hours.toFixed(1)} color="cyan" />
        <StatCard icon={Lightning} label={isSpanish ? 'Horas Facturables' : 'Billable Hours'} value={analyticsData.billable_hours.toFixed(1)} color="green" />
        <StatCard icon={Clock} label={isSpanish ? 'No Facturables' : 'Non-Billable'} value={analyticsData.non_billable_hours.toFixed(1)} color="gold" />
        <StatCard icon={ChartLine} label={isSpanish ? 'Tasa de Facturabilidad' : 'Billability Rate'} value={`${analyticsData.billability_rate}%`} color={analyticsData.billability_rate >= 70 ? 'green' : 'red'} />
      </div>

      {/* Categories Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Billable */}
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            {isSpanish ? 'Horas Facturables por Categoría' : 'Billable Hours by Category'}
          </h3>
          {billableCategories.length === 0 ? (
            <p className="text-[#9CA3AF] text-center py-4">{t('noData')}</p>
          ) : (
            <div className="space-y-4">
              {billableCategories.map((cat) => {
                const pct = analyticsData.total_hours > 0 ? (cat.hours / analyticsData.total_hours * 100) : 0;
                return (
                  <div key={cat.category}>
                    <div className="flex justify-between mb-1">
                      <span className="text-[#F3F4F6] text-sm">{cat.label}</span>
                      <span className="text-[#9CA3AF] text-sm">{cat.hours.toFixed(1)}h ({pct.toFixed(1)}%)</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Non-Billable */}
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <Warning className="w-5 h-5 text-amber-400" />
            {isSpanish ? 'Horas No Facturables por Categoría' : 'Non-Billable Hours by Category'}
          </h3>
          {nonBillableCategories.length === 0 ? (
            <p className="text-[#9CA3AF] text-center py-4">{t('noData')}</p>
          ) : (
            <div className="space-y-4">
              {nonBillableCategories.map((cat) => {
                const pct = analyticsData.total_hours > 0 ? (cat.hours / analyticsData.total_hours * 100) : 0;
                return (
                  <div key={cat.category}>
                    <div className="flex justify-between mb-1">
                      <span className="text-[#F3F4F6] text-sm">{cat.label}</span>
                      <span className="text-[#9CA3AF] text-sm">{cat.hours.toFixed(1)}h ({pct.toFixed(1)}%)</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Hours by Status */}
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Horas por Estado de Aprobación' : 'Hours by Approval Status'}</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-[#0B0E14] rounded-lg">
            <p className="text-2xl font-bold text-emerald-400">{(analyticsData.by_status?.aprobada || 0).toFixed(1)}h</p>
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Aprobadas' : 'Approved'}</p>
          </div>
          <div className="text-center p-4 bg-[#0B0E14] rounded-lg">
            <p className="text-2xl font-bold text-amber-400">{(analyticsData.by_status?.pendiente || 0).toFixed(1)}h</p>
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Pendientes' : 'Pending'}</p>
          </div>
          <div className="text-center p-4 bg-[#0B0E14] rounded-lg">
            <p className="text-2xl font-bold text-red-400">{(analyticsData.by_status?.rechazada || 0).toFixed(1)}h</p>
            <p className="text-sm text-[#9CA3AF]">{isSpanish ? 'Rechazadas' : 'Rejected'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Page Component
export function CapacityPlanningPage() {
  const { t, isSpanish } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [capacityData, setCapacityData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeksAhead, setWeeksAhead] = useState(4);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');

  const fetchCapacity = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCapacityPlanning({ weeks_ahead: weeksAhead });
      setCapacityData(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t, weeksAhead]);

  const fetchHeatmap = useCallback(async () => {
    try {
      const response = await getCapacityHeatmap({ weeks: 8 });
      setHeatmapData(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await getTimeAnalytics({ period: analyticsPeriod });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [analyticsPeriod]);

  useEffect(() => {
    fetchCapacity();
  }, [fetchCapacity]);

  useEffect(() => {
    if (activeTab === 'heatmap') fetchHeatmap();
    if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab, fetchAnalytics, fetchHeatmap]);

  return (
    <Layout title={isSpanish ? 'Planificación de Capacidad' : 'Capacity Planning'}>
      <div className="space-y-6" data-testid="capacity-planning-page">
        {/* Weeks Selector */}
        <div className="flex justify-end">
          <Select value={weeksAhead.toString()} onValueChange={(v) => setWeeksAhead(parseInt(v))}>
            <SelectTrigger className="w-48 valhalla-input">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="2">{isSpanish ? '2 semanas' : '2 weeks'}</SelectItem>
              <SelectItem value="4">{isSpanish ? '4 semanas' : '4 weeks'}</SelectItem>
              <SelectItem value="6">{isSpanish ? '6 semanas' : '6 weeks'}</SelectItem>
              <SelectItem value="8">{isSpanish ? '8 semanas' : '8 weeks'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0B0E14] border border-[#242E42] p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Users className="w-4 h-4 mr-2" />
              {isSpanish ? 'Capacidad' : 'Capacity'}
            </TabsTrigger>
            <TabsTrigger value="heatmap" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <ChartBar className="w-4 h-4 mr-2" />
              {isSpanish ? 'Mapa de Calor' : 'Heatmap'}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <ChartLine className="w-4 h-4 mr-2" />
              {isSpanish ? 'Análisis de Tiempo' : 'Time Analytics'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <CapacityOverview data={capacityData} loading={loading} />
          </TabsContent>
          
          <TabsContent value="heatmap" className="mt-6">
            <CapacityHeatmapTab heatmapData={heatmapData} loading={!heatmapData} />
          </TabsContent>
          
          <TabsContent value="analytics" className="mt-6">
            <TimeAnalyticsTab 
              analyticsData={analyticsData} 
              loading={!analyticsData} 
              period={analyticsPeriod}
              onPeriodChange={setAnalyticsPeriod}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default CapacityPlanningPage;
